// 品質表示スコープの明細グリッド(組成126/法定外表示127/付記用語128/構成部品129/デメリット130)の
// 表示行絞り込み。加工指示入力の buildQualityRepresentativeDisplayRows と同じ規則:
//   選択中アイテム/グループの行 → 代表カラー(選択カラー or 先頭カラー)の行 → 内容キーで重複排除。
// 品番マスタではグリッドの行データ自体は全件のまま保持し(保存データを欠落させないため)、
// AG Grid の外部フィルタで表示行だけを絞る。
// panelType ごとの表示対象rowid集合のキャッシュ(行配列の同一性+選択コンテキストで無効化)。
// サービスは呼び出し毎に生成されるためモジュールで保持する。
const visibleRowIdCache = new Map();

export function createQualityScopeDisplayService(deps) {
    const {
        DicVal,
        isEmpty,
        GridData,
        resolveKakousijiGridPanelByType,
        kakousijiQualityGroupControlStateRef,
        getColumnInfo
    } = deps;

    // 加工指示の QUALITY_PANEL_FIELD_MAP(119-123)を品番マスタのtype(+7)へ写したもの。
    const QUALITY_SCOPE_FIELD_MAP = {
        "126": { item: "SSDQ_ITMCITEMCD", group: "SSDQ_GROUPNO", groupFallback: "SSDQ_FUKUSIZAICOLORGRP", color: "SSDQ_COLORCD", order: "SSDQ_HYOJIJUN", invalid: "SSDQ_INVALID", no: "SSDQ_NO" },
        "127": { item: "SSDH_ITMCITEMCD", group: "SSDH_GROUPNO", groupFallback: "SSDH_FUKUSIZAICOLORGRP", color: "SSDH_COLORCD", order: "SSDH_HYOJIJUN", invalid: "SSDH_INVALID", no: "SSDH_NO" },
        "128": { item: "SSDY_ITMCITEMCD", group: "SSDY_GROUPNO", groupFallback: "SSDY_FUKUSIZAICOLORGRP", color: "SSDY_COLORCD", order: "SSDY_HYOJIJUN", invalid: "SSDY_INVALID", no: "SSDY_NO" },
        "129": { item: "SSDB_ITMCITEMCD", group: "SSDB_GROUPNO", groupFallback: "SSDB_FUKUSIZAICOLORGRP", color: "SSDB_COLORCD", order: "SSDB_HYOJIJUN", invalid: "SSDB_INVALID", no: "SSDB_NO" },
        "130": { item: "SSDD_ITMCITEMCD", group: "SSDD_GROUPNO", groupFallback: "SSDD_FUKUSIZAICOLORGRP", color: "SSDD_COLORCD", order: "SSDD_HYOJIJUN", invalid: "SSDD_INVALID", no: "SSDD_NO" },
        // 洗濯表示(131)は WashGrid(先頭1行を表示行へ変換)のため外部フィルタ対象外。代表行の選択にのみ使う。
        "131": { item: "SSDE_ITMCITEMCD", group: "SSDE_GROUPNO", groupFallback: "SSDE_FUKUSIZAICOLORGRP", color: "SSDE_COLORCD", order: "SSDE_PATANKBN", invalid: "SSDE_INVALID", no: "SSDE_NO", washOnly: true }
    };
    const NON_CONTENT_COLUMN_IDS = ["ROWID", "INDEX", "GRIP", "EXPAND", "ROW_KIND"];

    function normalizeText(value) {
        return String(value ?? "").trim();
    }
    function upper(value) {
        return normalizeText(value).toUpperCase();
    }
    // "code:name" 形式からコード部を取り出す。
    function extractCode(value) {
        const text = normalizeText(value);
        if (text === "") return "";
        return text.split(":")[0].trim();
    }
    // 桁揃え違い('1'と'01')を同一視する比較キー。
    function compareKey(value) {
        const text = extractCode(value);
        if (text === "") return "";
        return /^\d+$/.test(text) ? String(Number(text)) : text;
    }
    function readRowValueIgnoreCase(row, columnId) {
        if (isEmpty(row) || isEmpty(columnId)) return undefined;
        const target = upper(columnId);
        const key = Object.keys(row).find((k) => upper(k) === target);
        return isEmpty(key) ? undefined : row[key];
    }
    function isQualityScopeDisplayPanelType(panelType) {
        const map = QUALITY_SCOPE_FIELD_MAP[normalizeText(panelType)];
        return !isEmpty(map) && !map.washOnly;
    }
    function isQualityScopeDisplayPanel(panelInfo) {
        return isQualityScopeDisplayPanelType(DicVal(panelInfo, "type", ""));
    }
    function readSelectionContext() {
        const ctx = kakousijiQualityGroupControlStateRef.current || {};
        return {
            itemKey: compareKey(DicVal(ctx, "itemCd", "")),
            groupKey: compareKey(DicVal(ctx, "groupNo", "")),
            colorKey: compareKey(DicVal(ctx, "colorCd", ""))
        };
    }
    function readRowContext(row, map) {
        const groupRaw = readRowValueIgnoreCase(row, map.group);
        const groupValue = normalizeText(groupRaw) !== "" ? groupRaw : readRowValueIgnoreCase(row, map.groupFallback);
        return {
            itemKey: compareKey(readRowValueIgnoreCase(row, map.item)),
            groupKey: compareKey(groupValue),
            colorKey: compareKey(readRowValueIgnoreCase(row, map.color)),
            invalid: normalizeText(readRowValueIgnoreCase(row, map.invalid)).toLowerCase() === "1"
                || normalizeText(readRowValueIgnoreCase(row, map.invalid)).toLowerCase() === "true"
        };
    }
    // 内容比較に使う列(物理列のうちキー列以外)。
    function contentColumnIds(panelType, map) {
        const type = normalizeText(panelType);
        const excluded = new Set([
            ...NON_CONTENT_COLUMN_IDS,
            upper(map.no), upper(map.item), upper(map.group), upper(map.groupFallback),
            upper(map.color), upper(map.order), upper(map.invalid)
        ].filter((id) => id !== ""));
        const columninfo = typeof getColumnInfo === "function" ? getColumnInfo() : [];
        const ids = [];
        (Array.isArray(columninfo) ? columninfo : []).forEach((col) => {
            if (normalizeText(DicVal(col, "type", "")) !== type) return;
            if (normalizeText(DicVal(col, "table", "")) === "-") return; // UI専用列は内容比較に含めない
            const cid = upper(DicVal(col, "columnid", ""));
            if (cid === "" || excluded.has(cid) || ids.includes(cid)) return;
            ids.push(cid);
        });
        return ids.sort((a, b) => a.localeCompare(b));
    }
    function contentKey(row, panelType, map, columnIds) {
        const orderValue = normalizeText(readRowValueIgnoreCase(row, map.order));
        const contentPart = columnIds
            .map((cid) => `${cid}=${normalizeText(readRowValueIgnoreCase(row, cid))}`)
            .join("");
        return `${normalizeText(panelType)}${orderValue}${contentPart}`;
    }
    // 代表行(選択アイテム/グループ→代表カラー→内容キー重複排除)を返す
    // (加工指示 buildQualityRepresentativeDisplayRows 相当)。
    function pickQualityScopeRepresentativeRows(panelType, rows) {
        const map = QUALITY_SCOPE_FIELD_MAP[normalizeText(panelType)];
        if (isEmpty(map)) return [];
        const selection = readSelectionContext();
        if (selection.itemKey === "" || selection.groupKey === "") return [];
        const contextRows = (Array.isArray(rows) ? rows : []).filter((row) => {
            if (isEmpty(row)) return false;
            const rc = readRowContext(row, map);
            return !rc.invalid && rc.itemKey === selection.itemKey && rc.groupKey === selection.groupKey && rc.colorKey !== "";
        });
        if (contextRows.length === 0) return [];
        const colors = [];
        contextRows.forEach((row) => {
            const colorKey = readRowContext(row, map).colorKey;
            if (!colors.includes(colorKey)) colors.push(colorKey);
        });
        const representativeColor = selection.colorKey !== "" && colors.includes(selection.colorKey)
            ? selection.colorKey
            : colors[0];
        const representativeRows = contextRows
            .filter((row) => readRowContext(row, map).colorKey === representativeColor)
            .sort((a, b) => {
                const ao = Number(normalizeText(readRowValueIgnoreCase(a, map.order)));
                const bo = Number(normalizeText(readRowValueIgnoreCase(b, map.order)));
                if (Number.isFinite(ao) && Number.isFinite(bo) && ao !== bo) return ao - bo;
                return normalizeText(DicVal(a, "rowid", "")).localeCompare(normalizeText(DicVal(b, "rowid", "")), "ja", { numeric: true });
            });
        const columnIds = contentColumnIds(panelType, map);
        const seen = new Set();
        const out = [];
        representativeRows.forEach((row) => {
            const key = contentKey(row, panelType, map, columnIds);
            if (seen.has(key)) return;
            seen.add(key);
            out.push(row);
        });
        return out;
    }
    // 表示対象行のrowid集合を計算する。
    function computeVisibleRowIds(panelType, rows) {
        const visible = new Set();
        pickQualityScopeRepresentativeRows(panelType, rows).forEach((row) => {
            visible.add(normalizeText(DicVal(row, "rowid", "")));
        });
        return visible;
    }
    function buildQualityScopeFilterVersion(panelType) {
        const selection = readSelectionContext();
        return [normalizeText(panelType), selection.itemKey, selection.groupKey, selection.colorKey].join("|");
    }
    // AG Grid 外部フィルタ: 行が表示対象か。
    function isQualityScopeRowVisible(panelType, row) {
        const type = normalizeText(panelType);
        if (!isQualityScopeDisplayPanelType(type)) return true;
        const panelInfo = resolveKakousijiGridPanelByType(type);
        const rows = isEmpty(panelInfo) ? [] : (GridData(panelInfo) || []);
        // 保持行に無い行(グリッド初期化時の空行や新規追加行)は絞り込み対象外で常に表示する。
        // 初期空行まで隠すと AG Grid の firstDataRendered が発火せずグリッドAPIが登録されないため、
        // 以後の行投入が届かずグリッドが空のままになる。
        const rowId = normalizeText(DicVal(row, "rowid", ""));
        if (!Array.isArray(rows) || rows.length === 0) return true;
        if (!rows.some((stored) => normalizeText(DicVal(stored, "rowid", "")) === rowId)) return true;
        const version = buildQualityScopeFilterVersion(type);
        const cached = visibleRowIdCache.get(type);
        let visible;
        if (!isEmpty(cached) && cached.rows === rows && cached.version === version && cached.length === rows.length) {
            visible = cached.visible;
        } else {
            visible = computeVisibleRowIds(type, rows);
            visibleRowIdCache.set(type, { rows, version, length: rows.length, visible });
        }
        return visible.has(normalizeText(DicVal(row, "rowid", "")));
    }

    // ---- デメリット(130)の表示列組み立て(加工指示 demeritPanelService.normalizeDemeritRow 相当) ----
    // 表示列は UI 専用(SELECTEDDEMERITKIND / SSDD_DMTCD_NAME_DMY / SSDD_DMTCD_HUDA_DMY)で、
    // DB列(SSDD_NAMEFLG / SSDD_HUDAFLAG / SSDD_DMTCD)から種別とコードを補完する。
    const DEMERIT_PANEL_TYPE = "130";
    const DEMERIT_KIND_FIELD = "SELECTEDDEMERITKIND";
    const DEMERIT_NAME_FLG = "SSDD_NAMEFLG";
    const DEMERIT_HUDA_FLG = "SSDD_HUDAFLAG";
    const DEMERIT_DMT_CD = "SSDD_DMTCD";
    const DEMERIT_NAME_DMY = "SSDD_DMTCD_NAME_DMY";
    const DEMERIT_HUDA_DMY = "SSDD_DMTCD_HUDA_DMY";
    const DEMERIT_KIND_NAME = "1";
    const DEMERIT_KIND_HUDA = "2";
    const DEMERIT_KIND_LABELS = { [DEMERIT_KIND_NAME]: "ネーム", [DEMERIT_KIND_HUDA]: "札" };
    function isDemeritFlagOn(value) {
        const text = normalizeText(value).toLowerCase();
        return text === "1" || text === "true" || text === "yes";
    }
    function resolveDemeritKind(row) {
        const current = extractCode(readRowValueIgnoreCase(row, DEMERIT_KIND_FIELD));
        if (current === DEMERIT_KIND_NAME || current === DEMERIT_KIND_HUDA) return current;
        if (isDemeritFlagOn(readRowValueIgnoreCase(row, DEMERIT_NAME_FLG))) return DEMERIT_KIND_NAME;
        if (isDemeritFlagOn(readRowValueIgnoreCase(row, DEMERIT_HUDA_FLG))) return DEMERIT_KIND_HUDA;
        return "";
    }
    function normalizeDemeritDisplayRow(row) {
        const next = { ...(isEmpty(row) ? {} : row) };
        const kind = resolveDemeritKind(next);
        const dmtCd = extractCode(readRowValueIgnoreCase(next, DEMERIT_DMT_CD));
        // 加工指示入力のセレクト列表示(「1 ネーム」)に合わせる
        next[DEMERIT_KIND_FIELD] = kind === "" ? "" : `${kind} ${DEMERIT_KIND_LABELS[kind]}`;
        if (kind === DEMERIT_KIND_NAME) {
            next[DEMERIT_NAME_DMY] = normalizeText(DicVal(next, DEMERIT_NAME_DMY, "")) || dmtCd;
            next[DEMERIT_HUDA_DMY] = "";
        } else if (kind === DEMERIT_KIND_HUDA) {
            next[DEMERIT_HUDA_DMY] = normalizeText(DicVal(next, DEMERIT_HUDA_DMY, "")) || dmtCd;
            next[DEMERIT_NAME_DMY] = "";
        } else {
            next[DEMERIT_NAME_DMY] = "";
            next[DEMERIT_HUDA_DMY] = "";
        }
        return next;
    }
    function normalizeDemeritDisplayRows(rows) {
        if (!Array.isArray(rows)) return [];
        return rows.map((row) => normalizeDemeritDisplayRow(row));
    }
    function isDemeritScopePanelType(panelType) {
        return normalizeText(panelType) === DEMERIT_PANEL_TYPE;
    }

    return {
        isQualityScopeDisplayPanelType,
        isQualityScopeDisplayPanel,
        buildQualityScopeFilterVersion,
        isQualityScopeRowVisible,
        isDemeritScopePanelType,
        normalizeDemeritDisplayRows,
        pickQualityScopeRepresentativeRows
    };
}
